import React, { useState } from 'react';
import { BookOpen, Heart, Syringe, FileText, AlertTriangle, Search, ChevronDown, ChevronUp } from 'lucide-react';
import Layout from '../components/Layout';

const QuickReference = () => {
  const [activeTab, setActiveTab] = useState('vitals');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCard, setExpandedCard] = useState(null);

  const tabs = [
    { id: 'vitals', label: 'Vitals', icon: Heart },
    { id: 'vaccines', label: 'Vaccines', icon: Syringe },
    { id: 'abbreviations', label: 'Abbreviations', icon: FileText },
    { id: 'emergency', label: 'Emergency', icon: AlertTriangle },
  ];

  // Normal Vital Ranges
  const vitalsData = {
    dogs: {
      title: '🐕 Dogs',
      color: 'bg-blue-50 border-blue-200',
      vitals: [
        { name: 'Temperature', normal: '101-102.5°F (38.3-39.2°C)', notes: 'Rectal' },
        { name: 'Heart Rate', normal: '60-140 bpm', notes: 'Small dogs: 100-140, Large dogs: 60-100' },
        { name: 'Respiratory Rate', normal: '10-30 breaths/min', notes: 'At rest' },
        { name: 'Capillary Refill', normal: '< 2 seconds', notes: 'Press gum, watch pink return' },
        { name: 'Blood Pressure', normal: '110-160 mmHg systolic', notes: 'Doppler or oscillometric' },
        { name: 'Mucous Membranes', normal: 'Pink & moist', notes: 'Check gums' },
      ]
    },
    cats: {
      title: '🐈 Cats',
      color: 'bg-purple-50 border-purple-200',
      vitals: [
        { name: 'Temperature', normal: '100.5-102.5°F (38-39.2°C)', notes: 'Rectal' },
        { name: 'Heart Rate', normal: '140-220 bpm', notes: 'Stress can elevate significantly' },
        { name: 'Respiratory Rate', normal: '20-30 breaths/min', notes: 'At rest' },
        { name: 'Capillary Refill', normal: '< 2 seconds', notes: 'Check gums or inner lip' },
        { name: 'Blood Pressure', normal: '120-170 mmHg systolic', notes: 'White coat syndrome common' },
        { name: 'Mucous Membranes', normal: 'Pink & moist', notes: 'Pale = anemia, Yellow = jaundice' },
      ]
    },
    puppiesKittens: {
      title: '🍼 Puppies & Kittens',
      color: 'bg-pink-50 border-pink-200',
      vitals: [
        { name: 'Temperature (newborn)', normal: '95-99°F', notes: 'Gradually increases to adult range' },
        { name: 'Heart Rate', normal: '200-250 bpm', notes: 'Higher than adults' },
        { name: 'Respiratory Rate', normal: '15-40 breaths/min', notes: 'Variable' },
        { name: 'Eyes Open', normal: '10-14 days', notes: 'Do not force open' },
        { name: 'Ears Open', normal: '14-17 days', notes: '' },
        { name: 'Walking', normal: '3-4 weeks', notes: 'Wobbly at first' },
      ]
    }
  };

  // Vaccine Schedules
  const vaccineData = {
    dogs: {
      title: '🐕 Dog Vaccines',
      color: 'bg-green-50 border-green-200',
      vaccines: [
        { name: 'DHPP/DAPP', schedule: '6-8, 10-12, 14-16 weeks; booster at 1 year', frequency: 'Every 1-3 years', type: 'Core' },
        { name: 'Rabies', schedule: '12-16 weeks; booster at 1 year', frequency: 'Every 1-3 years (by law)', type: 'Core' },
        { name: 'Bordetella', schedule: 'As early as 8 weeks', frequency: 'Every 6-12 months', type: 'Non-core' },
        { name: 'Leptospirosis', schedule: '12 & 14-16 weeks', frequency: 'Annual', type: 'Non-core' },
        { name: 'Lyme', schedule: '12 & 14-16 weeks', frequency: 'Annual', type: 'Non-core' },
        { name: 'Canine Influenza', schedule: '6-8 weeks, booster 2-4 weeks later', frequency: 'Annual', type: 'Non-core' },
      ]
    },
    cats: {
      title: '🐈 Cat Vaccines',
      color: 'bg-orange-50 border-orange-200',
      vaccines: [
        { name: 'FVRCP', schedule: '6-8, 10-12, 14-16 weeks; booster at 1 year', frequency: 'Every 1-3 years', type: 'Core' },
        { name: 'Rabies', schedule: '12-16 weeks; booster at 1 year', frequency: 'Every 1-3 years', type: 'Core' },
        { name: 'FeLV', schedule: '8 & 12 weeks (kittens)', frequency: 'Annual for at-risk cats', type: 'Core for kittens' },
        { name: 'FIV', schedule: 'No longer recommended', frequency: 'N/A', type: 'Not recommended' },
        { name: 'Bordetella', schedule: 'Single dose', frequency: 'Annual if needed', type: 'Non-core' },
      ]
    }
  };

  // Common Abbreviations
  const abbreviations = [
    { abbr: 'SID/QD', meaning: 'Once daily', category: 'Frequency' },
    { abbr: 'BID', meaning: 'Twice daily', category: 'Frequency' },
    { abbr: 'TID', meaning: 'Three times daily', category: 'Frequency' },
    { abbr: 'QID', meaning: 'Four times daily', category: 'Frequency' },
    { abbr: 'PRN', meaning: 'As needed', category: 'Frequency' },
    { abbr: 'EOD', meaning: 'Every other day', category: 'Frequency' },
    { abbr: 'PO', meaning: 'By mouth (oral)', category: 'Route' },
    { abbr: 'SQ/SC', meaning: 'Subcutaneous', category: 'Route' },
    { abbr: 'IM', meaning: 'Intramuscular', category: 'Route' },
    { abbr: 'IV', meaning: 'Intravenous', category: 'Route' },
    { abbr: 'NPO', meaning: 'Nothing by mouth', category: 'Route' },
    { abbr: 'TPR', meaning: 'Temperature, Pulse, Respiration', category: 'Vitals' },
    { abbr: 'HR', meaning: 'Heart rate', category: 'Vitals' },
    { abbr: 'RR', meaning: 'Respiratory rate', category: 'Vitals' },
    { abbr: 'BP', meaning: 'Blood pressure', category: 'Vitals' },
    { abbr: 'CRT', meaning: 'Capillary refill time', category: 'Vitals' },
    { abbr: 'MM', meaning: 'Mucous membranes', category: 'Vitals' },
    { abbr: 'BCS', meaning: 'Body condition score', category: 'Assessment' },
    { abbr: 'Dx', meaning: 'Diagnosis', category: 'Assessment' },
    { abbr: 'Hx', meaning: 'History', category: 'Assessment' },
    { abbr: 'Sx', meaning: 'Surgery/Symptoms', category: 'Assessment' },
    { abbr: 'Tx', meaning: 'Treatment', category: 'Assessment' },
    { abbr: 'Rx', meaning: 'Prescription', category: 'Assessment' },
    { abbr: 'FeLV', meaning: 'Feline Leukemia Virus', category: 'Disease' },
    { abbr: 'FIV', meaning: 'Feline Immunodeficiency Virus', category: 'Disease' },
    { abbr: 'DHPP', meaning: 'Distemper, Hepatitis, Parvo, Parainfluenza', category: 'Disease' },
    { abbr: 'UTI', meaning: 'Urinary tract infection', category: 'Disease' },
    { abbr: 'GI', meaning: 'Gastrointestinal', category: 'Anatomy' },
    { abbr: 'ADR', meaning: 'Ain\'t doing right', category: 'Other' },
    { abbr: 'BAR', meaning: 'Bright, alert, responsive', category: 'Other' },
    { abbr: 'QAR', meaning: 'Quiet, alert, responsive', category: 'Other' },
  ];

  // Emergency Info
  const emergencyData = [
    {
      title: '🚨 CPR - Dogs & Cats',
      color: 'bg-red-50 border-red-300',
      steps: [
        'Check for responsiveness - call name, tap',
        'Check breathing - watch chest, feel for breath',
        'Check pulse - femoral artery (inner thigh)',
        'If no pulse: Start compressions',
        'Rate: 100-120 compressions/min',
        'Depth: 1/3 to 1/2 chest width',
        'Give 2 breaths every 30 compressions',
        'Continue until vet care available'
      ]
    },
    {
      title: '⚡ Shock Signs',
      color: 'bg-orange-50 border-orange-300',
      steps: [
        'Pale or white gums',
        'Rapid, weak pulse',
        'Cool extremities',
        'Rapid breathing',
        'Weakness/collapse',
        'Delayed CRT (>2 seconds)',
        'ACTION: Keep warm, minimize stress, get to vet ASAP'
      ]
    },
    {
      title: '🩸 Bleeding Control',
      color: 'bg-yellow-50 border-yellow-300',
      steps: [
        'Apply direct pressure with clean cloth',
        'Elevate wound above heart if possible',
        'Maintain pressure for 5-10 minutes',
        'If soaked through, add more - don\'t remove',
        'Tourniquet ONLY as last resort (note time)',
        'Get to emergency vet immediately'
      ]
    },
    {
      title: '🤢 Toxin Ingestion',
      color: 'bg-purple-50 border-purple-300',
      steps: [
        'Identify the toxin if possible',
        'Call ASPCA Poison Control: (888) 426-4435',
        'DO NOT induce vomiting unless directed',
        'Bring product packaging to vet',
        'Common toxins: chocolate, xylitol, grapes, lilies (cats), antifreeze, rodenticides'
      ]
    },
    {
      title: '🌡️ Heatstroke',
      color: 'bg-red-50 border-red-300',
      steps: [
        'Move to cool area immediately',
        'Apply cool (not cold) water to body',
        'Focus on neck, armpits, groin',
        'Offer small amounts of water',
        'DO NOT use ice - causes vasoconstriction',
        'Get to vet even if seems recovered'
      ]
    },
    {
      title: '😰 Seizure Response',
      color: 'bg-blue-50 border-blue-300',
      steps: [
        'Stay calm, time the seizure',
        'Move objects away from pet',
        'DO NOT put hands near mouth',
        'Keep room quiet and dim',
        'Note: duration, movements, recovery',
        'Seizure >5 min = EMERGENCY',
        'Cluster seizures = EMERGENCY'
      ]
    }
  ];

  const filteredAbbreviations = abbreviations.filter(
    a => a.abbr.toLowerCase().includes(searchTerm.toLowerCase()) ||
         a.meaning.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [...new Set(abbreviations.map(a => a.category))];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-brand-brown flex items-center">
            <BookOpen className="w-8 h-8 mr-3 text-pastel-blue" />
            Quick Reference
          </h1>
          <p className="text-brand-brown/60 mt-1">Essential info at your fingertips</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex overflow-x-auto space-x-2 mb-6 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-pastel-pink to-pastel-purple text-white'
                  : 'bg-white border-2 border-brand-brown/20 text-brand-brown hover:border-pastel-purple'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Vitals Tab */}
        {activeTab === 'vitals' && (
          <div className="space-y-4">
            {Object.values(vitalsData).map((species, idx) => (
              <div key={idx} className={`card p-4 border-2 ${species.color}`}>
                <h3 className="font-bold text-brand-brown text-lg mb-3">{species.title}</h3>
                <div className="space-y-2">
                  {species.vitals.map((vital, vIdx) => (
                    <div key={vIdx} className="flex flex-col sm:flex-row sm:items-center py-2 border-b border-brand-brown/10 last:border-0">
                      <span className="font-medium text-brand-brown w-40">{vital.name}</span>
                      <span className="text-pastel-purple font-semibold flex-1">{vital.normal}</span>
                      {vital.notes && (
                        <span className="text-sm text-brand-brown/60 mt-1 sm:mt-0">{vital.notes}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Vaccines Tab */}
        {activeTab === 'vaccines' && (
          <div className="space-y-4">
            {Object.values(vaccineData).map((species, idx) => (
              <div key={idx} className={`card p-4 border-2 ${species.color}`}>
                <h3 className="font-bold text-brand-brown text-lg mb-3">{species.title}</h3>
                <div className="space-y-3">
                  {species.vaccines.map((vaccine, vIdx) => (
                    <div key={vIdx} className="bg-white/50 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <span className="font-semibold text-brand-brown">{vaccine.name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          vaccine.type === 'Core' ? 'bg-green-200 text-green-800' :
                          vaccine.type === 'Non-core' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {vaccine.type}
                        </span>
                      </div>
                      <p className="text-sm text-brand-brown/70 mt-1">
                        <strong>Initial:</strong> {vaccine.schedule}
                      </p>
                      <p className="text-sm text-pastel-purple">
                        <strong>Booster:</strong> {vaccine.frequency}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Abbreviations Tab */}
        {activeTab === 'abbreviations' && (
          <div>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-brown/40" />
              <input
                type="text"
                placeholder="Search abbreviations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>

            {categories.map(category => {
              const items = filteredAbbreviations.filter(a => a.category === category);
              if (items.length === 0) return null;
              
              return (
                <div key={category} className="mb-4">
                  <h3 className="text-sm font-medium text-brand-brown/60 mb-2 uppercase tracking-wide">
                    {category}
                  </h3>
                  <div className="card p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {items.map((item, idx) => (
                        <div key={idx} className="flex items-center p-2 rounded-lg hover:bg-pastel-pink/10">
                          <span className="font-bold text-pastel-purple w-16">{item.abbr}</span>
                          <span className="text-brand-brown">{item.meaning}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Emergency Tab */}
        {activeTab === 'emergency' && (
          <div className="space-y-4">
            <div className="card p-3 bg-red-100 border-2 border-red-300 mb-4">
              <p className="text-red-800 font-semibold text-center">
                🚨 In case of emergency, always contact a veterinarian immediately!
              </p>
            </div>
            
            {emergencyData.map((card, idx) => (
              <div key={idx} className={`card border-2 ${card.color} overflow-hidden`}>
                <button
                  onClick={() => setExpandedCard(expandedCard === idx ? null : idx)}
                  className="w-full p-4 flex items-center justify-between"
                >
                  <h3 className="font-bold text-brand-brown text-lg">{card.title}</h3>
                  {expandedCard === idx ? (
                    <ChevronUp className="w-5 h-5 text-brand-brown/60" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-brand-brown/60" />
                  )}
                </button>
                
                {expandedCard === idx && (
                  <div className="px-4 pb-4">
                    <ol className="space-y-2">
                      {card.steps.map((step, sIdx) => (
                        <li key={sIdx} className="flex items-start">
                          <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-sm font-bold text-brand-brown mr-3 flex-shrink-0">
                            {sIdx + 1}
                          </span>
                          <span className="text-brand-brown">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            ))}
            
            {/* Emergency Contacts */}
            <div className="card p-4 bg-white border-2 border-brand-brown/20">
              <h3 className="font-bold text-brand-brown mb-3">📞 Emergency Numbers</h3>
              <div className="space-y-2">
                <p className="flex justify-between">
                  <span className="text-brand-brown">ASPCA Poison Control:</span>
                  <a href="tel:8884264435" className="text-pastel-purple font-semibold">(888) 426-4435</a>
                </p>
                <p className="flex justify-between">
                  <span className="text-brand-brown">Pet Poison Helpline:</span>
                  <a href="tel:8555647764" className="text-pastel-purple font-semibold">(855) 764-7764</a>
                </p>
                <p className="text-sm text-brand-brown/60 mt-2">* Consultation fees may apply</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default QuickReference;
